// Route time-window helpers for the project "Routes" tab.
// Kept dependency-free (no helpers.js import) so it is unit-testable without pulling
// helpers.js's DOM import chain — same reason routeListStatus.js lives on its own.
//
// FOJW steers on the ETA at the LAST CUSTOMER (not the arrival back at the depot /
// "end station"), so these expose that value and a "too late" threshold for it.

// A customer stop is anything that is not the depot start, depot end, or a break.
// Same predicate as isNotEmptyRoute / completedStopsCount / getRouteListStatus.
const isCustomerStop = stop =>
  stop && stop.type !== "start" && stop.type !== "end" && stop.type !== "break";

// A stop is "done" when finished or skipped — same rule as routeListStatus / the progress bar.
const isStopDone = stop =>
  stop && (stop.status === "finished" || stop.status === "skipped");

// Last customer stop in visiting order (route.stops is already ordered), regardless of status.
export const getLastCustomerStop = stops => {
  const customers = (stops || []).filter(isCustomerStop);
  return customers.length ? customers[customers.length - 1] : null;
};

// ETA (arr_time, seconds-of-day) at the last *not-yet-done* customer — the last stop the driver
// still has to reach, which is what FOJW steers the route end time on. Done (finished/skipped)
// stops are excluded so a stop completed OUT OF PLANNED ORDER (e.g. the last-in-sequence stop
// finished early) doesn't leave its stale planned ETA as the route's end time. Falls back to the
// last customer's ETA once every customer is done. Null if there are no customers / no arr_time.
export const getLastCustomerEta = stops => {
  const customers = (stops || []).filter(isCustomerStop);
  if (!customers.length) return null;
  const remaining = customers.filter(stop => !isStopDone(stop));
  const target = remaining.length
    ? remaining[remaining.length - 1]
    : customers[customers.length - 1];
  return target && target.arr_time != null ? target.arr_time : null;
};

// FOJW "bijsturen" threshold: a last-customer ETA at/after 16:00 is the signal that a
// route may need adjusting. Mirrors checkShouldRouteUnitsBecomeBolder (units >= 140) —
// hardcoded the same way. % 86400 keeps it aligned with the secondsToTime (hours % 24)
// display for the rare route that runs past midnight.
export const LATE_ETA_SECONDS = 16 * 3600; // 16:00

export const isLastCustomerEtaLate = etaSeconds =>
  etaSeconds != null && etaSeconds % 86400 >= LATE_ETA_SECONDS;
